/**
 * Starter templates for the cloud alarm-rule authoring UI.
 *
 * Each template is a function from "selected target devices" to a
 * `RuleDraft` (an `AlarmRule` without `id`/`template_id`/timestamps —
 * the editor stamps those when the user saves).
 *
 * Templates are READ-ONLY in v1: editing one diverges into a normal
 * rule with no link back to the source template. We don't persist
 * user-customised templates here.
 */
import type { AlarmRule } from "./wireFormat";

export type TargetableDevice = {
  id: string;
  name: string;
  /** Topic-glob hint of what telemetry this device exposes — for
   *  picking matching default datapoints. */
  topicGlobs?: string[];
};

export type RuleDraft = Omit<
  AlarmRule,
  | "id"
  | "createdAt"
  | "updatedAt"
  // v4 fields are zod-defaulted on parse, so templates may omit them.
  // The editor reads back the zod-defaulted shape after the form mounts,
  // at which point AlarmRule's auto_clear + propagate are populated.
  | "auto_clear"
  | "propagate"
  | "notification"
> & {
  auto_clear?: AlarmRule["auto_clear"];
  propagate?: AlarmRule["propagate"];
  notification?: AlarmRule["notification"];
  /** Marker so the SPA can tell freshly-templated drafts from
   *  envelope-loaded rules. Stripped before send. */
  __draft: true;
};

export type RuleTemplate = {
  key: string;
  name: string;
  description: string;
  category: "safety" | "communications" | "operations" | "performance";
  /**
   * Build a draft for the given devices. Implementations should resolve
   * the most appropriate datapoint topic per device using the device's
   * `topicGlobs`; if none match, the template falls back to a sensible
   * default and the editor's datapoint picker lets the operator fix it.
   */
  build: (target: TargetableDevice) => RuleDraft;
};

const allHours: boolean[][] = Array.from({ length: 7 }, () =>
  Array.from({ length: 24 }, () => true),
);
const offHoursWeekdays: boolean[][] = Array.from({ length: 7 }, (_, dayIdx) =>
  Array.from({ length: 24 }, (_, hour) => {
    if (dayIdx >= 5) return false; // weekend = off-hours
    return hour < 7 || hour >= 19; // outside 07:00-19:00 = off-hours
  }),
);

function pickTopic(
  target: TargetableDevice,
  matchSuffix: string,
  fallback: string,
): string {
  const globs = target.topicGlobs ?? [];
  const match = globs.find((g) => g.toLowerCase().includes(matchSuffix.toLowerCase()));
  return match ?? fallback;
}

/**
 * v4 — opt-in defaults that templates fold in without per-template boilerplate.
 *
 * - `auto_clear: true` — every template fires/clears on the condition by default
 *   (sticky alarms are opt-in per rule).
 * - `propagate: "TENANT"` — visible to every tenant admin, matching today's
 *   TB-EMS dashboard expectation. Override per rule for multi-tenant setups.
 * - `notification` — CRITICAL and MAJOR templates pre-populate an EMPTY
 *   inline notification block so the editor opens with the Recipients
 *   panel already expanded; the operator sees the "add at least one
 *   email" warning immediately and can't accidentally save without
 *   wiring an alert. Lower severities don't pre-populate (operator
 *   often wants no email — they just want the alarm visible in TB).
 */
function withV4Defaults(rule: Omit<RuleDraft, "__draft">): RuleDraft {
  const needsEmail = rule.severity === "CRITICAL" || rule.severity === "MAJOR";
  return {
    __draft: true,
    auto_clear: true,
    propagate: "TENANT",
    notification: needsEmail
      ? { instant: [], notify_on_clear: false }
      : undefined,
    ...rule,
  };
}

export const ALARM_RULE_TEMPLATES: RuleTemplate[] = [
  {
    key: "high_ambient_temp",
    name: "BESS High Ambient Temperature",
    description: "Fires when enclosure ambient stays above 50 °C for 60 s.",
    category: "safety",
    build: (target) => withV4Defaults({
      name: `${target.name} – High ambient temperature`,
      type: "threshold",
      enabled: true,
      severity: "CRITICAL",
      datapoint: pickTopic(target, "ambient_temperature", "battery/measurement/1/ambient_temperature"),
      config: {
        operator: ">",
        threshold: 50,
        hysteresis: 2,
        duration_s: 60,
        unit: "°C",
      },
      alarm_message: "Ambient temperature high",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "low_soc",
    name: "BESS Low SOC",
    description: "Fires when SoC drops below 10% with 5pp hysteresis.",
    category: "operations",
    build: (target) => withV4Defaults({
      name: `${target.name} – Low SOC`,
      type: "threshold",
      enabled: true,
      severity: "MAJOR",
      datapoint: pickTopic(target, "soc", "battery/measurement/1/SOC"),
      config: {
        operator: "<",
        threshold: 10,
        hysteresis: 5,
        duration_s: 0,
        unit: "%",
      },
      alarm_message: "Battery state of charge low",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "high_soc",
    name: "BESS High SOC",
    description: "Fires when SoC exceeds 95%.",
    category: "operations",
    build: (target) => withV4Defaults({
      name: `${target.name} – High SOC`,
      type: "threshold",
      enabled: true,
      severity: "MINOR",
      datapoint: pickTopic(target, "soc", "battery/measurement/1/SOC"),
      config: {
        operator: ">",
        threshold: 95,
        hysteresis: 2,
        duration_s: 0,
        unit: "%",
      },
      alarm_message: "Battery state of charge high",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "comm_loss",
    name: "BESS Communication Loss",
    description: "Fires when the BESS hasn't reported SoC for 120 s.",
    category: "communications",
    build: (target) => withV4Defaults({
      name: `${target.name} – Communication loss`,
      type: "inactivity",
      enabled: true,
      severity: "CRITICAL",
      datapoint: pickTopic(target, "soc", "battery/measurement/1/SOC"),
      config: { timeout_s: 120 },
      alarm_message: "BESS stopped reporting telemetry",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "sample_drop",
    name: "Sample-rate Degradation",
    description: "Fires when ≥ 30% of expected samples are missing over 60 s.",
    category: "communications",
    build: (target) => withV4Defaults({
      name: `${target.name} – Sample-rate degradation`,
      type: "comm_degradation",
      enabled: true,
      severity: "MAJOR",
      datapoint: pickTopic(target, "soc", "battery/measurement/1/SOC"),
      config: {
        expected_rate_hz: 1,
        window_s: 60,
        loss_pct_threshold: 30,
        duration_s: 30,
      },
      alarm_message: "Telemetry sample loss above threshold",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "grid_comm_loss",
    name: "Grid Communication Loss",
    description: "Fires when grid meter hasn't reported for 60 s.",
    category: "communications",
    build: (target) => withV4Defaults({
      name: `${target.name} – Grid communication loss`,
      type: "inactivity",
      enabled: true,
      severity: "CRITICAL",
      datapoint: pickTopic(target, "grid_w", "smartmeter/aggregation/grid_W"),
      config: { timeout_s: 60 },
      alarm_message: "Grid meter stopped reporting",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "inverter_fault",
    name: "Inverter Fault",
    description: "Triggers when any inverter fault flag goes true.",
    category: "safety",
    build: (target) => withV4Defaults({
      name: `${target.name} – Inverter fault`,
      type: "boolean",
      enabled: true,
      severity: "MAJOR",
      datapoint: pickTopic(target, "fault", "battery/measurement/1/inverter_fault"),
      config: { alarm_when: true, duration_s: 0 },
      alarm_message: "Inverter reported fault",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "total_voltage_low",
    name: "Battery Total Voltage Low",
    description: "DC-bus voltage below site-specific minimum.",
    category: "safety",
    build: (target) => withV4Defaults({
      name: `${target.name} – Total voltage low`,
      type: "threshold",
      enabled: true,
      severity: "MAJOR",
      datapoint: pickTopic(target, "total_voltage", "battery/measurement/1/Total_Voltage"),
      config: { operator: "<", threshold: 700, hysteresis: 10, duration_s: 5, unit: "V" },
      alarm_message: "Battery DC bus voltage low",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "total_voltage_high",
    name: "Battery Total Voltage High",
    description: "DC-bus voltage above site-specific maximum.",
    category: "safety",
    build: (target) => withV4Defaults({
      name: `${target.name} – Total voltage high`,
      type: "threshold",
      enabled: true,
      severity: "MAJOR",
      datapoint: pickTopic(target, "total_voltage", "battery/measurement/1/Total_Voltage"),
      config: { operator: ">", threshold: 900, hysteresis: 10, duration_s: 5, unit: "V" },
      alarm_message: "Battery DC bus voltage high",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "setpoint_mismatch",
    name: "Setpoint vs Actual Mismatch",
    description: "Fires when actual power deviates >5 kW from setpoint for 30 s.",
    category: "performance",
    build: (target) => withV4Defaults({
      name: `${target.name} – Setpoint vs actual mismatch`,
      type: "expression",
      enabled: true,
      severity: "MINOR",
      datapoint: undefined,
      config: {
        expression: "abs(setpoint - actual) > 5000 and bess_on",
        datapoints: {
          setpoint: pickTopic(target, "setpoint", "controller/1/output/power_W"),
          actual: pickTopic(target, "ac_active_power", "battery/measurement/1/ac_active_power"),
          bess_on: pickTopic(target, "bess_onoff", "controller/1/output/BESS_ONOFF"),
        },
        duration_s: 30,
      },
      alarm_message: "BESS not tracking commanded setpoint",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "cell_voltage_imbalance",
    name: "Cell Voltage Imbalance",
    description: "Fires when cell voltage spread exceeds 0.1 V for 30 s.",
    category: "safety",
    build: (target) => withV4Defaults({
      name: `${target.name} – Cell voltage imbalance`,
      type: "expression",
      enabled: true,
      severity: "MAJOR",
      datapoint: undefined,
      config: {
        expression: "max(c1, c2, c3, c4) - min(c1, c2, c3, c4) > 0.1",
        datapoints: {
          c1: "battery/cell/1/voltage",
          c2: "battery/cell/2/voltage",
          c3: "battery/cell/3/voltage",
          c4: "battery/cell/4/voltage",
        },
        duration_s: 30,
      },
      alarm_message: "Cell voltage spread exceeds 100 mV",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "cell_temp_gradient",
    name: "Cell Temperature Gradient",
    description: "Fires when cell temp spread exceeds 8 °C.",
    category: "safety",
    build: (target) => withV4Defaults({
      name: `${target.name} – Cell temperature gradient`,
      type: "expression",
      enabled: true,
      severity: "MAJOR",
      datapoint: undefined,
      config: {
        expression: "max(t1, t2, t3, t4) - min(t1, t2, t3, t4) > 8",
        datapoints: {
          t1: "battery/cell/1/temperature",
          t2: "battery/cell/2/temperature",
          t3: "battery/cell/3/temperature",
          t4: "battery/cell/4/temperature",
        },
        duration_s: 60,
      },
      alarm_message: "Cell temperature gradient exceeds 8 °C",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "excessive_cycling",
    name: "Excessive Charge/Discharge Cycling",
    description: "Fires when BESS performs more than 6 charge cycles per hour.",
    category: "performance",
    build: (target) => withV4Defaults({
      name: `${target.name} – Excessive cycling`,
      type: "excessive_cycling",
      enabled: true,
      severity: "MINOR",
      datapoint: pickTopic(target, "ac_active_power", "battery/measurement/1/ac_active_power"),
      config: { max_cycles: 6, timeframe: "1h", power_threshold: 1000 },
      alarm_message: "BESS cycling at an unhealthy rate",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "peak_demand",
    name: "Peak Demand Approaching",
    description: "Fires when grid demand exceeds 85% of contract.",
    category: "performance",
    build: (target) => withV4Defaults({
      name: `${target.name} – Peak demand warning`,
      type: "peak_demand",
      enabled: true,
      severity: "WARNING",
      datapoint: pickTopic(target, "grid_w", "smartmeter/aggregation/grid_W"),
      config: {
        demand_limit: 100_000,
        warning_percent: 85,
        duration_s: 30,
      },
      alarm_message: "Grid demand approaching contract limit",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "idle_consumption",
    name: "Idle Consumption Off-Hours",
    description: "Fires when site draws > 5 kW outside operating hours.",
    category: "performance",
    build: (target) => withV4Defaults({
      name: `${target.name} – Idle consumption off-hours`,
      type: "idle_consumption",
      enabled: true,
      severity: "MINOR",
      datapoint: pickTopic(target, "grid_w", "smartmeter/aggregation/grid_W"),
      config: {
        power_threshold: 5000,
        weeklySchedule: offHoursWeekdays.map((d) => d.map((h) => !h)),
        duration_s: 60,
      },
      alarm_message: "Idle consumption during off-hours",
      deliveryMethod: "notification",
    }),
  },
  {
    key: "charging_outside_window",
    name: "Charging Outside Allowed Window",
    description: "Fires if BESS draws power outside the night-only charging window.",
    category: "operations",
    build: (target) => withV4Defaults({
      name: `${target.name} – Charging outside window`,
      type: "threshold",
      enabled: true,
      severity: "MAJOR",
      datapoint: pickTopic(target, "ac_active_power", "battery/measurement/1/ac_active_power"),
      // active_when: night-only window (00..06 + 22..24).
      active_when: {
        weeklySchedule: allHours.map((day) =>
          day.map((_, h) => !(h < 6 || h >= 22)),
        ),
      },
      config: {
        operator: ">",
        threshold: 1000,
        hysteresis: 100,
        duration_s: 30,
        unit: "W",
      },
      alarm_message: "BESS charging outside night-only window",
      deliveryMethod: "notification",
    }),
  },
  // NOTE: there is intentionally NO `composite` starter template.
  // A composite rule references *other* rules by id via
  // `config.child_rule_ids`, so it can only be authored once its
  // children already exist. A one-click starter cannot bootstrap a
  // composite + its children in a single single-rule editor flow —
  // the previous "Comm Loss During Discharge" template tried to and
  // produced a composite pointing at child ids that were never
  // written, leaving a permanent "unknown child rule id" warning and
  // a rule that could never fire. Composites are fully supported via
  // "New rule → type: composite", where <CompositeRuleBuilder> lets
  // the operator pick from the rules already present on the device.
];

export function findTemplate(key: string): RuleTemplate | undefined {
  return ALARM_RULE_TEMPLATES.find((t) => t.key === key);
}
